/* eslint-disable camelcase */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQueries } from "@tanstack/react-query";
import { client } from "@johnpaul/jp-api-client";

import {
  APIBlockItem,
  APICardComponent,
  ComponentData,
  JPClientRequest,
} from "@companion-core/shared/app/Interfaces/API";
import {
  COMPONENTS_PAGE_SIZE,
  TYPE_BUTTON,
  TYPE_CARD,
} from "@companion-core/shared/app/Utils/componentHelper";
import { hasSlideImage } from "./postListJourneyLayout.utils";

type BlockItemComponentsResponse = JPClientRequest<ComponentData>;

const getButtonProps = (component?: ComponentData) => {
  if (!component || !("button_title" in component)) {
    return {};
  }

  return {
    button_title: component.button_title,
    button_link: component.button_link,
    button_type: component.button_type,
  };
};

const hasCardContent = (component: ComponentData): boolean =>
  component.type !== TYPE_BUTTON &&
  (Boolean(component.title) || Boolean(component.description) || Boolean(component.medias?.length));

export const mapBlockItemToJourneySlide = (
  blockItem: APIBlockItem,
  components: ComponentData[]
): ComponentData => {
  const button = components.find((component) => component.type === TYPE_BUTTON);
  const card =
    components.find((component) => component.type === TYPE_CARD) ?? components.find(hasCardContent);

  return {
    id: blockItem.id,
    parent_id: blockItem.content_fk ?? blockItem.id,
    content_ct: blockItem.content_ct ?? TYPE_CARD,
    type: TYPE_CARD,
    state: card?.state ?? "undefined",
    title: card?.title ?? "",
    description: card?.description,
    medias: card?.medias,
    order: blockItem.order,
    ...getButtonProps(button),
  } as APICardComponent & ComponentData;
};

export const mapBlockItemsToJourneySlides = (
  blockItems: APIBlockItem[],
  fetchedComponentsByBlockItemId: Record<string, ComponentData[]> = {}
): ComponentData[] =>
  [...blockItems]
    .sort((firstItem, secondItem) => firstItem.order - secondItem.order)
    .map((blockItem) => {
      const components =
        blockItem.components.data.length > 0
          ? blockItem.components.data
          : (fetchedComponentsByBlockItemId[blockItem.id] ?? []);

      return mapBlockItemToJourneySlide(blockItem, components as ComponentData[]);
    })
    .filter((slide) => Boolean(slide.title) || Boolean(slide.description) || hasSlideImage(slide));

export const useJourneySlides = (blockItems: APIBlockItem[]) => {
  const { i18n } = useTranslation();
  const lang = i18n?.languages[0]?.toLocaleLowerCase() ?? "fr";

  const sortedBlockItems = useMemo(
    () => [...blockItems].sort((firstItem, secondItem) => firstItem.order - secondItem.order),
    [blockItems]
  );

  const blockItemsToFetch = useMemo(
    () => sortedBlockItems.filter((blockItem) => blockItem.components.data.length === 0),
    [sortedBlockItems]
  );

  const fetchedComponentsQueries = useQueries({
    queries: blockItemsToFetch.map((blockItem) => ({
      queryKey: ["block-item-components", blockItem.id, lang],
      queryFn: () =>
        client.content.v2.blocks.getBlockItemComponents(blockItem.id, {
          lang,
          page_size: COMPONENTS_PAGE_SIZE,
          page: 1,
        }),
      select: (response: BlockItemComponentsResponse) => response.data.results,
    })),
  });

  const fetchedComponentsByBlockItemId = useMemo(() => {
    return blockItemsToFetch.reduce<Record<string, ComponentData[]>>(
      (accumulator, blockItem, index) => {
        const components = fetchedComponentsQueries[index]?.data;
        if (components?.length) {
          accumulator[blockItem.id] = components;
        }
        return accumulator;
      },
      {}
    );
  }, [blockItemsToFetch, fetchedComponentsQueries]);

  const slides = useMemo(
    () => mapBlockItemsToJourneySlides(sortedBlockItems, fetchedComponentsByBlockItemId),
    [sortedBlockItems, fetchedComponentsByBlockItemId]
  );

  const isPending =
    blockItemsToFetch.length > 0 &&
    fetchedComponentsQueries.some((query) => query.isPending || query.isFetching);

  return {
    slides,
    isPending,
  };
};
